import type { Action } from '@reduxjs/toolkit';
import { sonner } from '@sr/ui-library';
import { useEffect } from 'react';
import { useAppDispatch } from '../stateStore';
import { ApiLoadingStateEnum } from '../utils/api/apiLoadingStateEnum';
import {
  isFailedState,
  isFinishedState,
  isSuccessState,
} from '../utils/helpers/common';

const { toast } = sonner;

type UseLoadingToastOptions = {
  /** Optional message shown when the loading state transitions to `failed`. */
  failureMessage?: string;
};

/**
 * Fires a success (and optionally failure) toast when an async loading state
 * transitions to a finished state, then dispatches `resetAction` to return the
 * slice to `idle` so the toast is not shown again on the next mount.
 *
 * Note: failure toasts are opt-in. When a slice already surfaces errors via a
 * global axios interceptor, leave `failureMessage` undefined.
 */
export function useLoadingToast(
  state: ApiLoadingStateEnum,
  successMessage: string,
  resetAction: () => Action,
  { failureMessage }: UseLoadingToastOptions = {},
) {
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (isSuccessState(state)) {
      toast.success(successMessage);
    } else if (failureMessage && isFailedState(state)) {
      toast.error(failureMessage);
    }

    if (isFinishedState(state)) {
      dispatch(resetAction());
    }
  }, [dispatch, failureMessage, resetAction, state, successMessage]);
}
